import {
  PLATFORMER_SCREEN_HEIGHT,
  PLATFORMER_SCREEN_WIDTH,
  type BlockSpawn,
  type CoinSpawn,
  type EnemySpawn,
  type LevelDefinition,
  type PipeSpawn,
  type ParallaxLayerMetadata,
  type ScenicObject,
  type TileRun,
} from '../types';

const TILE = 16;
const GROUND_ROW = 8;

function groundRuns(levelId: string, segments: readonly (readonly [number, number])[]): TileRun[] {
  const runs: TileRun[] = [];
  segments.forEach(([start, end], index) => {
    runs.push({
      id: `${levelId}-ground-top-${index}`,
      tile: 'ground-top',
      x: start,
      y: GROUND_ROW,
      length: end - start,
      collision: 'solid',
      renderLayer: 'playfield',
    });
    runs.push({
      id: `${levelId}-ground-fill-${index}`,
      tile: 'ground-fill',
      x: start,
      y: GROUND_ROW + 1,
      length: end - start,
      collision: 'solid',
      renderLayer: 'playfield',
    });
  });
  return runs;
}

function platformRun(id: string, x: number, y: number, length: number): TileRun {
  return {
    id,
    tile: 'brick-platform',
    x,
    y,
    length,
    collision: 'solid',
    renderLayer: 'playfield',
  };
}

function stairRun(id: string, x: number, y: number, length: number): TileRun {
  return {
    id,
    tile: 'stair-block',
    x,
    y,
    length,
    collision: 'solid',
    renderLayer: 'playfield',
  };
}

function block(
  id: string,
  kind: BlockSpawn['kind'],
  xTile: number,
  yTile: number,
  contains: BlockSpawn['contains'] = kind === 'question' ? 'coin' : 'empty',
): BlockSpawn {
  return {
    id,
    kind,
    x: xTile * TILE,
    y: yTile * TILE,
    contains,
    collision: 'solid',
    renderLayer: 'interactive',
  };
}

function pipe(id: string, xTile: number, heightTiles: number): PipeSpawn {
  return {
    id,
    x: xTile * TILE,
    y: (GROUND_ROW - heightTiles) * TILE,
    width: TILE * 2,
    height: heightTiles * TILE,
    collision: 'solid',
    renderLayer: 'interactive',
  };
}

function coin(id: string, xTile: number, yTile: number): CoinSpawn {
  return { id, x: xTile * TILE + 4, y: yTile * TILE + 3, renderLayer: 'pickups' };
}

function goomba(id: string, xTile: number, speed = 28): EnemySpawn {
  return {
    id,
    kind: 'goomba',
    x: xTile * TILE + 2,
    y: GROUND_ROW * TILE - 14,
    patrolSpeed: speed,
    activationPadding: 38,
    renderLayer: 'actors',
  };
}

function scenery(
  id: string,
  kind: ScenicObject['kind'],
  xTile: number,
  y: number,
  variant: number,
  parallax: number,
  renderLayer: ScenicObject['renderLayer'],
): ScenicObject {
  return {
    id,
    kind,
    x: xTile * TILE,
    y,
    variant,
    parallax,
    renderLayer,
    collision: 'none',
  };
}

const WORLD_1_1_WIDTH = 214;
const WORLD_1_2_WIDTH = 190;

const WORLD_PARALLAX_LAYERS: readonly ParallaxLayerMetadata[] = [
  {
    id: 'sky',
    assetId: 'sky-layer',
    sourceSize: { width: 240, height: 160 },
    displaySize: { width: 240, height: 160 },
    anchor: 'top-left',
    renderOrder: 0,
    scrollFactor: 0.08,
    repeatPolicy: 'repeat-x',
    collisionSource: false,
  },
  {
    id: 'far-bg',
    assetId: 'far-bg-layer',
    sourceSize: { width: 240, height: 160 },
    displaySize: { width: 240, height: 160 },
    anchor: 'top-left',
    renderOrder: 10,
    scrollFactor: 0.24,
    repeatPolicy: 'repeat-x',
    collisionSource: false,
  },
  {
    id: 'near-bg',
    assetId: 'near-bg-layer',
    sourceSize: { width: 240, height: 160 },
    displaySize: { width: 240, height: 160 },
    anchor: 'top-left',
    renderOrder: 20,
    scrollFactor: 0.52,
    repeatPolicy: 'repeat-x',
    collisionSource: false,
  },
];

export const WORLD_1_1: LevelDefinition = {
  id: 'world-1-1',
  worldLabel: '1-1',
  name: 'Mushroom Promenade',
  mapMode: 'side_scroll_mode',
  mapKind: 'platformer',
  visualModel: 'layered_tilemap',
  runtimeObjectModel: ['platform_objects', 'interactive_scene_objects', 'scene_hooks'],
  collisionModel: 'tile_collision+explicit_shapes+trigger_zones',
  engineTarget: 'raw_canvas',
  stageCanvas: {
    width: PLATFORMER_SCREEN_WIDTH,
    height: PLATFORMER_SCREEN_HEIGHT,
    tileSize: TILE,
    pixelScale: 1,
    topLeftAnchor: true,
  },
  world: {
    widthTiles: WORLD_1_1_WIDTH,
    heightTiles: 10,
    backgroundColor: '#5c94fc',
    timeLimit: 400,
  },
  parallaxLayers: WORLD_PARALLAX_LAYERS,
  layers: {
    tileRuns: [
      ...groundRuns('w11', [
        [0, 38],
        [41, 69],
        [72, 102],
        [106, 134],
        [137, 173],
        [177, WORLD_1_1_WIDTH],
      ]),
      platformRun('w11-platform-a', 44, 5, 5),
      platformRun('w11-platform-b', 74, 4, 4),
      platformRun('w11-platform-c', 112, 5, 6),
      platformRun('w11-platform-d', 146, 4, 4),
      stairRun('w11-stair-a1', 184, 7, 1),
      stairRun('w11-stair-a2', 185, 6, 2),
      stairRun('w11-stair-a3', 186, 5, 3),
      stairRun('w11-stair-a4', 187, 4, 4),
      stairRun('w11-stair-b1', 193, 4, 4),
      stairRun('w11-stair-b2', 194, 5, 3),
      stairRun('w11-stair-b3', 195, 6, 2),
      stairRun('w11-stair-b4', 196, 7, 1),
    ],
    blocks: [
      block('w11-q-01', 'question', 16, 5),
      block('w11-b-01', 'brick', 20, 5),
      block('w11-q-02', 'question', 21, 5),
      block('w11-b-02', 'brick', 22, 5),
      block('w11-q-03', 'question', 22, 2),
      block('w11-q-04', 'question', 55, 5),
      block('w11-b-03', 'brick', 56, 5),
      block('w11-q-05', 'question', 57, 5),
      block('w11-b-04', 'brick', 58, 5),
      block('w11-q-06', 'question', 87, 4),
      block('w11-b-05', 'brick', 91, 5),
      block('w11-b-06', 'brick', 92, 5),
      block('w11-q-07', 'question', 93, 5),
      block('w11-b-07', 'brick', 94, 5),
      block('w11-q-08', 'question', 126, 4),
      block('w11-q-09', 'question', 127, 4),
      block('w11-b-08', 'brick', 128, 4),
      block('w11-q-10', 'question', 158, 5),
      block('w11-b-09', 'brick', 159, 5),
      block('w11-b-10', 'brick', 160, 5),
    ],
    pipes: [
      pipe('w11-pipe-01', 28, 2),
      // Leave a fair landing/run-up after the first pit, especially for touch play.
      pipe('w11-pipe-02', 51, 3),
      pipe('w11-pipe-03', 82, 2),
      pipe('w11-pipe-04', 119, 3),
      pipe('w11-pipe-05', 153, 2),
    ],
    coins: [
      coin('w11-coin-01', 10, 6),
      coin('w11-coin-02', 11, 6),
      coin('w11-coin-03', 12, 6),
      coin('w11-coin-04', 44, 3),
      coin('w11-coin-05', 45, 3),
      coin('w11-coin-06', 46, 3),
      coin('w11-coin-07', 75, 2),
      coin('w11-coin-08', 76, 2),
      coin('w11-coin-09', 77, 2),
      coin('w11-coin-10', 108, 6),
      coin('w11-coin-11', 109, 5),
      coin('w11-coin-12', 110, 4),
      coin('w11-coin-13', 111, 5),
      coin('w11-coin-14', 112, 6),
      coin('w11-coin-15', 146, 2),
      coin('w11-coin-16', 147, 2),
      coin('w11-coin-17', 148, 2),
      coin('w11-coin-18', 172, 6),
      coin('w11-coin-19', 173, 5),
      coin('w11-coin-20', 174, 6),
    ],
    enemies: [
      goomba('w11-goomba-01', 22),
      goomba('w11-goomba-02', 35),
      goomba('w11-goomba-03', 53, 30),
      goomba('w11-goomba-04', 63, 30),
      goomba('w11-goomba-05', 79, 32),
      goomba('w11-goomba-06', 98, 32),
      goomba('w11-goomba-07', 115, 34),
      goomba('w11-goomba-08', 132, 34),
      goomba('w11-goomba-09', 150, 36),
      goomba('w11-goomba-10', 168, 36),
      goomba('w11-goomba-11', 180, 38),
    ],
    scenery: [
      scenery('w11-cloud-01', 'cloud', 8, 32, 0, 0.12, 'far-bg'),
      scenery('w11-cloud-02', 'cloud', 34, 22, 1, 0.12, 'far-bg'),
      scenery('w11-cloud-03', 'cloud', 66, 36, 0, 0.12, 'far-bg'),
      scenery('w11-cloud-04', 'cloud', 104, 24, 1, 0.12, 'far-bg'),
      scenery('w11-cloud-05', 'cloud', 148, 34, 0, 0.12, 'far-bg'),
      scenery('w11-cloud-06', 'cloud', 192, 20, 1, 0.12, 'far-bg'),
      scenery('w11-hill-01', 'hill', 6, 93, 0, 0.28, 'mid-bg'),
      scenery('w11-hill-02', 'hill', 52, 98, 1, 0.28, 'mid-bg'),
      scenery('w11-hill-03', 'hill', 101, 92, 0, 0.28, 'mid-bg'),
      scenery('w11-hill-04', 'hill', 164, 97, 1, 0.28, 'mid-bg'),
      scenery('w11-bush-01', 'bush', 19, 113, 0, 0.58, 'near-bg'),
      scenery('w11-bush-02', 'bush', 61, 113, 1, 0.58, 'near-bg'),
      scenery('w11-bush-03', 'bush', 124, 113, 0, 0.58, 'near-bg'),
      scenery('w11-bush-04', 'bush', 172, 113, 1, 0.58, 'near-bg'),
      scenery('w11-flag', 'flagpole', 204, 32, 0, 1, 'near-bg'),
      scenery('w11-castle', 'castle', 207, 80, 0, 1, 'near-bg'),
    ],
  },
  collision: {
    source: 'tile-runs-and-explicit-object-shapes',
    tileSize: TILE,
    pitKillY: 184,
  },
  sceneHooks: {
    playerSpawn: { x: 32, y: GROUND_ROW * TILE - 16 },
    checkpoints: [
      { id: 'w11-checkpoint-mid', triggerX: 103 * TILE, spawnX: 107 * TILE, spawnY: 112 },
      { id: 'w11-checkpoint-late', triggerX: 174 * TILE, spawnX: 178 * TILE, spawnY: 112 },
    ],
    cameraBounds: { x: 0, y: 0, width: WORLD_1_1_WIDTH * TILE, height: 160 },
    finishZone: { x: 204 * TILE, y: 28, width: 18, height: 108 },
    exitLink: 'world-1-2',
  },
};

export const WORLD_1_2: LevelDefinition = {
  id: 'world-1-2',
  worldLabel: '1-2',
  name: 'Bricktop Run',
  mapMode: 'side_scroll_mode',
  mapKind: 'platformer',
  visualModel: 'layered_tilemap',
  runtimeObjectModel: ['platform_objects', 'interactive_scene_objects', 'scene_hooks'],
  collisionModel: 'tile_collision+explicit_shapes+trigger_zones',
  engineTarget: 'raw_canvas',
  stageCanvas: {
    width: PLATFORMER_SCREEN_WIDTH,
    height: PLATFORMER_SCREEN_HEIGHT,
    tileSize: TILE,
    pixelScale: 1,
    topLeftAnchor: true,
  },
  world: {
    widthTiles: WORLD_1_2_WIDTH,
    heightTiles: 10,
    backgroundColor: '#5c94fc',
    timeLimit: 340,
  },
  parallaxLayers: WORLD_PARALLAX_LAYERS,
  layers: {
    tileRuns: [
      ...groundRuns('w12', [
        [0, 26],
        [29, 48],
        [52, 75],
        [78, 102],
        [106, 127],
        [131, 151],
        [155, WORLD_1_2_WIDTH],
      ]),
      platformRun('w12-platform-a', 18, 5, 5),
      platformRun('w12-platform-b', 34, 4, 6),
      platformRun('w12-platform-c', 56, 5, 4),
      platformRun('w12-platform-d', 81, 3, 6),
      platformRun('w12-platform-e', 112, 5, 7),
      platformRun('w12-platform-f', 137, 3, 5),
      stairRun('w12-stair-a1', 161, 7, 1),
      stairRun('w12-stair-a2', 162, 6, 2),
      stairRun('w12-stair-a3', 163, 5, 3),
      stairRun('w12-stair-a4', 164, 4, 4),
      stairRun('w12-stair-b1', 170, 4, 4),
      stairRun('w12-stair-b2', 171, 5, 3),
      stairRun('w12-stair-b3', 172, 6, 2),
      stairRun('w12-stair-b4', 173, 7, 1),
    ],
    blocks: [
      block('w12-q-01', 'question', 10, 5),
      block('w12-q-02', 'question', 11, 3),
      block('w12-b-01', 'brick', 12, 5),
      block('w12-b-02', 'brick', 13, 5),
      block('w12-q-03', 'question', 43, 5),
      block('w12-b-03', 'brick', 44, 5),
      block('w12-q-04', 'question', 45, 5),
      block('w12-q-05', 'question', 66, 4),
      block('w12-b-04', 'brick', 67, 4),
      block('w12-b-05', 'brick', 68, 4),
      block('w12-q-06', 'question', 96, 5),
      block('w12-q-07', 'question', 97, 5),
      block('w12-b-06', 'brick', 98, 5),
      block('w12-q-08', 'question', 122, 3),
      block('w12-b-07', 'brick', 123, 3),
      block('w12-q-09', 'question', 146, 5),
      block('w12-q-10', 'question', 147, 5),
    ],
    pipes: [
      pipe('w12-pipe-01', 24, 2),
      pipe('w12-pipe-02', 47, 3),
      pipe('w12-pipe-03', 73, 2),
      pipe('w12-pipe-04', 101, 3),
      pipe('w12-pipe-05', 126, 2),
      pipe('w12-pipe-06', 149, 3),
    ],
    coins: [
      coin('w12-coin-01', 18, 3),
      coin('w12-coin-02', 19, 3),
      coin('w12-coin-03', 20, 3),
      coin('w12-coin-04', 34, 2),
      coin('w12-coin-05', 35, 2),
      coin('w12-coin-06', 36, 2),
      coin('w12-coin-07', 56, 3),
      coin('w12-coin-08', 57, 3),
      coin('w12-coin-09', 82, 1),
      coin('w12-coin-10', 83, 1),
      coin('w12-coin-11', 84, 1),
      coin('w12-coin-12', 112, 3),
      coin('w12-coin-13', 113, 3),
      coin('w12-coin-14', 114, 3),
      coin('w12-coin-15', 137, 1),
      coin('w12-coin-16', 138, 1),
      coin('w12-coin-17', 139, 1),
      coin('w12-coin-18', 154, 5),
    ],
    enemies: [
      goomba('w12-goomba-01', 15, 32),
      goomba('w12-goomba-02', 22, 34),
      goomba('w12-goomba-03', 39, 34),
      goomba('w12-goomba-04', 49, 36),
      goomba('w12-goomba-05', 62, 36),
      goomba('w12-goomba-06', 76, 38),
      goomba('w12-goomba-07', 91, 38),
      goomba('w12-goomba-08', 104, 40),
      goomba('w12-goomba-09', 120, 40),
      goomba('w12-goomba-10', 134, 42),
      goomba('w12-goomba-11', 145, 42),
      goomba('w12-goomba-12', 158, 44),
    ],
    scenery: [
      scenery('w12-cloud-01', 'cloud', 6, 24, 1, 0.12, 'far-bg'),
      scenery('w12-cloud-02', 'cloud', 40, 34, 0, 0.12, 'far-bg'),
      scenery('w12-cloud-03', 'cloud', 73, 20, 1, 0.12, 'far-bg'),
      scenery('w12-cloud-04', 'cloud', 112, 36, 0, 0.12, 'far-bg'),
      scenery('w12-cloud-05', 'cloud', 151, 22, 1, 0.12, 'far-bg'),
      scenery('w12-hill-01', 'hill', 4, 96, 1, 0.28, 'mid-bg'),
      scenery('w12-hill-02', 'hill', 48, 91, 0, 0.28, 'mid-bg'),
      scenery('w12-hill-03', 'hill', 100, 97, 1, 0.28, 'mid-bg'),
      scenery('w12-hill-04', 'hill', 148, 92, 0, 0.28, 'mid-bg'),
      scenery('w12-bush-01', 'bush', 26, 113, 1, 0.58, 'near-bg'),
      scenery('w12-bush-02', 'bush', 70, 113, 0, 0.58, 'near-bg'),
      scenery('w12-bush-03', 'bush', 117, 113, 1, 0.58, 'near-bg'),
      scenery('w12-bush-04', 'bush', 158, 113, 0, 0.58, 'near-bg'),
      scenery('w12-flag', 'flagpole', 181, 32, 0, 1, 'near-bg'),
      scenery('w12-castle', 'castle', 184, 80, 1, 1, 'near-bg'),
    ],
  },
  collision: {
    source: 'tile-runs-and-explicit-object-shapes',
    tileSize: TILE,
    pitKillY: 184,
  },
  sceneHooks: {
    playerSpawn: { x: 32, y: GROUND_ROW * TILE - 16 },
    checkpoints: [
      { id: 'w12-checkpoint-mid', triggerX: 92 * TILE, spawnX: 93 * TILE, spawnY: 112 },
      { id: 'w12-checkpoint-late', triggerX: 153 * TILE, spawnX: 156 * TILE, spawnY: 112 },
    ],
    cameraBounds: { x: 0, y: 0, width: WORLD_1_2_WIDTH * TILE, height: 160 },
    finishZone: { x: 181 * TILE, y: 28, width: 18, height: 108 },
    exitLink: 'victory',
  },
};

export const PLATFORMER_LEVELS: readonly LevelDefinition[] = [WORLD_1_1, WORLD_1_2];
